import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import axios from 'axios';

import { API_URL_V2, GET_INTERCOM_MESSENGER_JWT } from '../constants/api';

/* global process */
const isolatedDev = Boolean(process.env.RN_API_BASE_URL);

// RefNow Intercom workspace.
// TODO: confirm these against the Intercom workspace settings (Settings →
// Installation) before release.
export const INTERCOM_APP_ID = 'ne9cbr2k';
const INTERCOM_REGION = 'eu';

// Refresh the JWT this many ms before it actually expires so Messenger never
// hits an expired token mid-session.
const REFRESH_SKEW_MS = 5 * 60 * 1000;

let hasBooted = false;
let refreshTimer = null;

// Fetch a fresh Messenger JWT for the current logged-in session. Cookies are
// sent the same way as every other authenticated call in the app.
async function fetchMessengerJwt() {
	const url = API_URL_V2 + GET_INTERCOM_MESSENGER_JWT;
	const { data } = await axios.get(url, { withCredentials: true });
	const payload = data?.data?.[0];

	if (!payload?.intercom_user_jwt) {
		throw new Error('Intercom Messenger JWT missing from response');
	}

	return payload; // { intercom_user_jwt, expires_at, expires_in, user_id }
}

function clearRefreshTimer() {
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

function scheduleRefresh(expiresInSeconds) {
	clearRefreshTimer();
	if (!expiresInSeconds) return;

	const delay = Math.max(expiresInSeconds * 1000 - REFRESH_SKEW_MS, 30 * 1000);
	refreshTimer = setTimeout(refreshMessengerToken, delay);
}

async function refreshMessengerToken() {
	try {
		const { intercom_user_jwt, expires_in } = await fetchMessengerJwt();
		update({ intercom_user_jwt });
		scheduleRefresh(expires_in);
	} catch (error) {
		// Refresh failed — leave the current session as-is. Messenger is optional,
		// so a stale token simply means it stops updating, never blocks the app.
		// eslint-disable-next-line no-console
		console.warn('[Intercom] token refresh failed', error);
	}
}

function deriveRole(roleid) {
	if (roleid === 3) return 'superadmin';
	if (roleid === 2) return 'admin';
	return 'user';
}

// Custom attributes here (user_role, team) must already exist in Intercom
// (Settings → Data → People) as Text attributes, or the boot/update call
// silently fails to store them. Named `user_role` (not `role`) because `role`
// collides with a reserved/default Intercom attribute.
function buildUserAttributes(account) {
	return {
		user_id: String(account.ouid),
		email: account.email,
		name: account.fullname,
		created_at: account.createdts || undefined,
		user_role: deriveRole(account.roleid),
		team: account.teamname || undefined,
		// TODO: `requests_created_by_user` was requested but has no backing data —
		// GET_EMPLOYER_ACCOUNT only returns an org-wide total (org_requests_created),
		// never a per-user count. Needs a backend field before this can be sent.
	};
}

// Custom company attributes here (total_credit_balance, total_requests_created,
// trial_credits) must already exist in Intercom (Settings → Data → Companies) as
// Number attributes — `company_id`/`name`/`created_at` are built-in and need no
// setup.
function buildCompany(account) {
	if (!account.orgid) return undefined;

	return {
		company_id: String(account.orgid),
		name: account.company || undefined,
		created_at: account.org_createdts || undefined,
		total_credit_balance: account.totalcreditbal ?? undefined,
		total_requests_created: account.org_requests_created ?? undefined,
		trial_credits: account.trial_credits ?? undefined,
	};
}

/**
 * Boot Intercom Messenger for a logged-in user using a secure JWT.
 *
 * `account` is the account object from GET_EMPLOYER_ACCOUNT — it must expose the
 * real `ouid` (which the backend JWT is issued against), plus email/fullname.
 * Safe to call on every account fetch: the first call boots, later calls only
 * refresh the user/company attributes (the scheduled refresh keeps the JWT valid).
 */
export async function bootIntercom(account) {
	if (isolatedDev) return;
	if (!account?.ouid) return;

	if (hasBooted) {
		update({
			...buildUserAttributes(account),
			company: buildCompany(account),
		});
		return;
	}

	try {
		const { intercom_user_jwt, expires_in } = await fetchMessengerJwt();

		Intercom({
			app_id: INTERCOM_APP_ID,
			region: INTERCOM_REGION,
			...buildUserAttributes(account),
			company: buildCompany(account),
			intercom_user_jwt,
		});

		hasBooted = true;
		scheduleRefresh(expires_in);
	} catch (error) {
		// Never block the app on Intercom. If the JWT request or boot fails we just
		// leave Messenger disabled for this session. Log quietly to aid debugging.
		// eslint-disable-next-line no-console
		console.warn('[Intercom] boot failed — Messenger disabled this session', error);
	}
}

// When an indirect page is opened by a company/portal user (e.g. an employer
// adding a referee on a candidate's behalf via `?iscompany=1`) we must NOT show
// the Messenger to them — it's meant for the candidate/referee only.
function isCompanyContext() {
	return new URLSearchParams(window.location.search).get('iscompany') === '1';
}

/**
 * Boot Intercom Messenger for an indirect, non-logged-in user — a candidate on
 * /addReferees or a referee on /addReference.
 *
 * Deliberately boots as a fully ANONYMOUS visitor: no user_id, no email, no
 * name, no custom attributes. With Messenger Security (JWT) enforced on the
 * workspace, ANY identified boot — even email-only — is rejected unless it
 * carries a valid JWT ("identity_verification_missing_user_hash"). Anonymous
 * visitor boots are the only unauthenticated path, so we send identity-free
 * boots and let Intercom collect the person's email in-conversation instead.
 *
 * Do NOT add email/name/custom attributes here without also supplying a JWT —
 * it will blank the Messenger again while enforcement is on.
 *
 * Does nothing when the page is opened with ?iscompany=1 (an employer acting on
 * the candidate's behalf), or when a Messenger session has already booted.
 */
export function bootIntercomAnonymous() {
	if (isolatedDev) return;
	if (isCompanyContext()) return;
	if (hasBooted) return;

	try {
		Intercom({
			app_id: INTERCOM_APP_ID,
			region: INTERCOM_REGION,
		});

		hasBooted = true;
	} catch (error) {
		// Never block the page on Intercom.
		// eslint-disable-next-line no-console
		console.warn('[Intercom] anonymous boot failed', error);
	}
}

// Tear down Intercom on logout so the next user doesn't inherit this session.
export function shutdownIntercom() {
	if (isolatedDev) return;
	clearRefreshTimer();
	hasBooted = false;
	shutdown();
}
